import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Building2, Store, Users, Package, TrendingUp, Activity, Globe, Shield, ExternalLink, Warehouse, Calculator, Inbox } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { colors, getNeumorphicShadow, getNeumorphicHover } from '@/lib/designSystem';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

// Launcher presentation only — WHICH apps exist, what they are CALLED and WHERE
// they live all come from the canonical backend registry (GET /api/applications),
// so preview launches preview apps and production launches production ones. An
// app with no presentation entry or no server-provided launch_url gets no tile.
const APP_PRESENTATION = {
  INVENTORY: {
    description: 'Stock & consumption',
    icon: Warehouse,
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
  },
  PEOPLE_ENGINE: {
    description: 'People, attendance & roster',
    icon: Users,
    color: 'from-sky-500 to-sky-600',
    bgColor: 'bg-sky-50',
  },
  SALES_CAPTURE: {
    description: 'Sales upload & validation',
    icon: TrendingUp,
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
  },
  ACCOUNTS: {
    description: 'Finance & reconciliation',
    icon: Calculator,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
  },
  // 1OS Bills (CR-P1; formerly Bulk Capture Console). Requires BOTH user
  // authorisation (allowed_apps BILLS) AND org entitlement (enabled_modules BILLS).
  // SSO `module` stays the legacy key until the old client is retired.
  BILLS: {
    description: 'Bill capture, review & confirmation',
    module: 'bulk_capture_console',
    icon: Inbox,
    color: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-50',
    requiresSso: true,
    requiredAppCode: 'BILLS',
    requiredOrgModule: 'BILLS',
  },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, hasAppAccess, hasOrgModule } = useAuth();
  const [stats, setStats] = useState({
    organizations: 0,
    outlets: 0,
    users: 0,
    vendors: 0,
  });
  const [loading, setLoading] = useState(true);
  const [monthlyBillData, setMonthlyBillData] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [isPlatformView, setIsPlatformView] = useState(false);
  const [orgBreakdown, setOrgBreakdown] = useState(null);
  const [launchingApp, setLaunchingApp] = useState(null); // app.code currently being launched (SSO mint in-flight)
  const [launcherApps, setLauncherApps] = useState([]);
  const { theme } = useTheme();
  const currentColors = colors[theme];

  // Check if user is super admin
  const isSuperAdmin = user?.is_super_admin === true;

  // Launcher click handler — for apps that require SSO handoff, mint a token
  // via /api/auth/sso/issue-token and redirect with combined ?token=…#token=….
  // For legacy tiles (no requiresSso) keep the original anchor behaviour.
  const handleAppLaunch = async (app, e) => {
    if (!app.requiresSso) return; // let the <a> navigate normally
    e.preventDefault();
    if (launchingApp) return;
    setLaunchingApp(app.code);
    try {
      const response = await api.issueSsoToken({
        redirect_uri: app.url,
        module: app.module,
      });
      const target = response?.data?.redirect_url_combined
        || response?.data?.redirect_url_fragment
        || `${app.url}?token=${encodeURIComponent(response?.data?.token || '')}`;
      window.open(target, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      console.error('SSO launch failed:', detail || err);
      alert(
        typeof detail === 'string'
          ? `Could not open ${app.name}: ${detail}`
          : `Could not open ${app.name}. Please ensure your admin has allowlisted ${app.url} for SSO.`
      );
    } finally {
      setLaunchingApp(null);
    }
  };

  useEffect(() => {
    loadDashboardStats();
    loadBillsData();
    loadLauncherApps();
  }, []);

  // The launcher's catalogue and display names come from the canonical registry.
  const loadLauncherApps = async () => {
    try {
      const res = await api.getApplications();
      const apps = (res.data?.applications || [])
        .filter((a) => APP_PRESENTATION[a.app_code] && a.launch_url)
        .map((a) => ({
          code: a.app_code,
          name: a.display_name,
          url: a.launch_url,
          ...APP_PRESENTATION[a.app_code],
        }));
      setLauncherApps(apps);
    } catch (error) {
      console.error('Error loading application registry:', error);
      setLauncherApps([]);
    }
  };

  const loadDashboardStats = async () => {
    try {
      // Use the new dashboard stats endpoint that handles scoping
      const response = await api.getDashboardStats();
      const data = response.data;
      
      setStats({
        organizations: data.organizations,
        outlets: data.outlets,
        users: data.users,
        vendors: data.vendors,
      });
      
      setUserGrowthData(data.user_growth || []);
      setIsPlatformView(data.is_platform_view || false);
      setOrgBreakdown(data.org_breakdown || null);
      
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // Fallback to legacy method if new endpoint fails
      await loadStatsLegacy();
    } finally {
      setLoading(false);
    }
  };

  // Legacy method as fallback
  const loadStatsLegacy = async () => {
    try {
      let vendorsData = [];
      try {
        const vendorsResponse = await api.getExternalVendors();
        vendorsData = vendorsResponse.data || [];
      } catch (e) {
        const vendorsResponse = await api.getVendors();
        vendorsData = vendorsResponse.data || [];
      }

      const [orgs, outlets, users] = await Promise.all([
        api.getOrganizations(),
        api.getOutlets(),
        api.getUsers(),
      ]);

      setStats({
        organizations: orgs.data.length,
        outlets: outlets.data.length,
        users: users.data.length,
        vendors: vendorsData.length,
      });
      
      // Generate mock growth data for fallback
      setUserGrowthData([
        { name: 'Week 1', users: Math.floor(users.data.length * 0.7) },
        { name: 'Week 2', users: Math.floor(users.data.length * 0.8) },
        { name: 'Week 3', users: Math.floor(users.data.length * 0.9) },
        { name: 'Week 4', users: users.data.length },
      ]);
    } catch (error) {
      console.error('Error loading stats (legacy):', error);
    }
  };

  const loadBillsData = async () => {
    try {
      // Bills come from the organisation-scoped Bills API; Control Room has no
      // local bill store to fall back to.
      const response = await api.getExternalBills();
      const bills = response.data;
      
      // Process bills to get monthly counts
      const monthlyData = {};
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      bills.forEach(bill => {
        if (bill.bill_date) {
          const date = new Date(bill.bill_date);
          const monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
          monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
        }
      });

      // Get last 6 months data
      const sortedMonths = Object.entries(monthlyData)
        .sort((a, b) => new Date(a[0]) - new Date(b[0]))
        .slice(-6);

      const chartData = sortedMonths.map(([month, count]) => ({
        name: month.split(' ')[0], // Just the month name
        value: count,
      }));

      setMonthlyBillData(chartData.length > 0 ? chartData : [
        { name: 'No Data', value: 0 }
      ]);
    } catch (error) {
      console.error('Error loading bills data:', error);
      // Fallback to empty data
      setMonthlyBillData([{ name: 'No Data', value: 0 }]);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Build stat cards based on user role
  const statCards = [];
  
  // Only show Organizations count for SUPER_ADMIN
  if (isSuperAdmin) {
    statCards.push({
      name: 'Organizations',
      value: stats.organizations,
      icon: Building2,
      gradient: `linear-gradient(135deg, ${currentColors.accentBlue}, ${currentColors.accentPurple})`,
      testId: 'stat-organizations',
      onClick: () => navigate('/organizations'),
    });
  }
  
  statCards.push(
    {
      name: 'Outlets',
      value: stats.outlets,
      icon: Store,
      gradient: `linear-gradient(135deg, ${currentColors.accentGreen}, ${currentColors.accentBlue})`,
      testId: 'stat-outlets',
      onClick: () => navigate('/outlets'),
    },
    {
      name: isSuperAdmin ? 'Platform Users' : 'Organization Users',
      value: stats.users,
      icon: Users,
      gradient: `linear-gradient(135deg, ${currentColors.accentPurple}, ${currentColors.accentBlue})`,
      testId: 'stat-users',
      onClick: () => navigate('/users'),
    },
    {
      name: 'Vendors',
      value: stats.vendors,
      icon: Package,
      gradient: `linear-gradient(135deg, ${currentColors.accentOrange}, ${currentColors.accentYellow})`,
      testId: 'stat-vendors',
      onClick: () => navigate('/masters'),
    }
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="dashboard-page">
      {/* Hero Section with Sync Status */}
      <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2 rounded-2xl p-6 md:p-8"
          style={{
            background: `linear-gradient(135deg, ${currentColors.accentBlue}, ${currentColors.accentPurple})`,
            boxShadow: getNeumorphicShadow(theme),
          }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {getGreeting()}! 👋
          </h1>
          <p className="text-white/90 text-lg">
            Welcome to your Control Room Dashboard
          </p>
        </motion.div>

        {/* Canonical supplier-bill source (EC mirror retired) */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-2xl p-6"
          style={{
            background: currentColors.cardBg,
            boxShadow: getNeumorphicShadow(theme),
          }}
          data-testid="bills-source-widget"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg" style={{ color: currentColors.textPrimary }}>
              Supplier Bill Source
            </h3>
            <Activity size={20} style={{ color: currentColors.accentBlue }} />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium" style={{ color: currentColors.textPrimary }}>
              1OS Bills — read on demand
            </div>
            <p className="text-xs" style={{ color: currentColors.textSecondary }}>
              Control Room reads canonical supplier-bill facts directly from Bills. The hourly
              Expense Capture mirror has been retired; no local copy is kept.
            </p>
          </div>
        </motion.div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <motion.div
            className="w-16 h-16 rounded-full"
            style={{
              border: `4px solid ${currentColors.accentBlue}`,
              borderTopColor: 'transparent',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      ) : (
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.name}
                  variants={itemVariants}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: getNeumorphicHover(theme),
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={stat.onClick}
                  data-testid={stat.testId}
                  className="rounded-2xl p-4 md:p-6 cursor-pointer"
                  style={{
                    background: currentColors.cardBg,
                    boxShadow: getNeumorphicShadow(theme),
                  }}
                >
                  <div className="flex flex-col items-center justify-center text-center space-y-3">
                    <motion.div
                      className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center"
                      style={{ background: stat.gradient }}
                      whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <Icon className="text-white" size={28} />
                    </motion.div>
                    <div>
                      <p
                        className="text-xs md:text-sm font-medium mb-1"
                        style={{ color: currentColors.textSecondary }}
                      >
                        {stat.name}
                      </p>
                      <motion.p
                        className="text-2xl md:text-3xl font-bold"
                        style={{ color: currentColors.textPrimary }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.1 + 0.3, type: 'spring' }}
                      >
                        {stat.value}
                      </motion.p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* 1OS Apps Launcher Section */}
          <motion.div
            variants={containerVariants}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ color: currentColors.textPrimary }}>
                1OS Apps
              </h2>
              <span className="text-sm" style={{ color: currentColors.textSecondary }}>
                Quick access to your modules
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {launcherApps
                .filter((app) => (!app.requiredAppCode || hasAppAccess(app.requiredAppCode))
                  && (!app.requiredOrgModule || hasOrgModule(app.requiredOrgModule)))
                .map((app, index) => {
                const AppIcon = app.icon;
                const isLaunching = launchingApp === app.code;
                return (
                  <motion.a
                    key={app.code}
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleAppLaunch(app, e)}
                    variants={itemVariants}
                    whileHover={{
                      scale: 1.05,
                      boxShadow: getNeumorphicHover(theme),
                    }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`app-tile-${app.code.toLowerCase()}`}
                    className={`rounded-2xl p-4 cursor-pointer relative group ${isLaunching ? 'opacity-60 pointer-events-none' : ''}`}
                    style={{
                      background: currentColors.cardBg,
                      boxShadow: getNeumorphicShadow(theme),
                    }}
                  >
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${app.color}`}>
                        <AppIcon className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: currentColors.textPrimary }}>
                          {app.name}
                        </p>
                        <p className="text-xs" style={{ color: currentColors.textSecondary }}>
                          {app.description}
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink size={14} style={{ color: currentColors.textSecondary }} />
                    </div>
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Activity Chart */}
            <motion.div
              variants={itemVariants}
              className="rounded-2xl p-6"
              style={{
                background: currentColors.cardBg,
                boxShadow: getNeumorphicShadow(theme),
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: currentColors.accentBlue }}
                >
                  <Activity size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg" style={{ color: currentColors.textPrimary }}>
                    Bills Activity
                  </h3>
                  <p className="text-sm" style={{ color: currentColors.textSecondary }}>
                    Total bills processed (last 6 months)
                  </p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyBillData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={currentColors.border} />
                  <XAxis dataKey="name" stroke={currentColors.textSecondary} />
                  <YAxis stroke={currentColors.textSecondary} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: currentColors.cardBg,
                      border: `1px solid ${currentColors.border}`,
                      borderRadius: '8px',
                      color: currentColors.textPrimary,
                    }}
                    labelStyle={{ color: currentColors.textPrimary }}
                    itemStyle={{ color: currentColors.accentBlue }}
                    formatter={(value) => [`${value} bills`, 'Total']}
                  />
                  <Bar dataKey="value" fill={currentColors.accentBlue} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>

            {/* User Growth Chart */}
            <motion.div
              variants={itemVariants}
              className="rounded-2xl p-6"
              style={{
                background: currentColors.cardBg,
                boxShadow: getNeumorphicShadow(theme),
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: currentColors.accentGreen }}
                >
                  {isPlatformView ? <Globe size={20} className="text-white" /> : <TrendingUp size={20} className="text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg" style={{ color: currentColors.textPrimary }}>
                      User Growth
                    </h3>
                    {isPlatformView && (
                      <span 
                        className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                        style={{ 
                          background: `${currentColors.accentPurple}20`,
                          color: currentColors.accentPurple
                        }}
                      >
                        <Shield size={12} />
                        Platform-wide
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: currentColors.textSecondary }}>
                    {isPlatformView 
                      ? 'Total users across all organizations' 
                      : 'Users in your organization'}
                  </p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={currentColors.border} />
                  <XAxis dataKey="name" stroke={currentColors.textSecondary} />
                  <YAxis stroke={currentColors.textSecondary} />
                  <Tooltip
                    contentStyle={{
                      background: currentColors.cardBg,
                      border: `1px solid ${currentColors.border}`,
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`${value} users`, 'Total']}
                  />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke={currentColors.accentGreen}
                    strokeWidth={3}
                    dot={{ fill: currentColors.accentGreen, r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          </div>

          {/* Organization Breakdown - Only for SUPER_ADMIN */}
          {isPlatformView && orgBreakdown && orgBreakdown.length > 0 && (
            <motion.div
              variants={itemVariants}
              className="rounded-2xl p-6 mb-8"
              style={{
                background: currentColors.cardBg,
                boxShadow: getNeumorphicShadow(theme),
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: currentColors.accentPurple }}
                >
                  <Building2 size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg" style={{ color: currentColors.textPrimary }}>
                      Users by Organization
                    </h3>
                    <span 
                      className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                      style={{ 
                        background: `${currentColors.accentPurple}20`,
                        color: currentColors.accentPurple
                      }}
                    >
                      <Shield size={12} />
                      Admin Only
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: currentColors.textSecondary }}>
                    Platform-wide user distribution across organizations
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={orgBreakdown.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={currentColors.border} />
                    <XAxis type="number" stroke={currentColors.textSecondary} />
                    <YAxis 
                      type="category" 
                      dataKey="org_name" 
                      stroke={currentColors.textSecondary}
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: currentColors.cardBg,
                        border: `1px solid ${currentColors.border}`,
                        borderRadius: '8px',
                      }}
                      formatter={(value) => [`${value} users`, 'Total']}
                    />
                    <Bar 
                      dataKey="user_count" 
                      fill={currentColors.accentPurple} 
                      radius={[0, 8, 8, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>

                {/* Organization List */}
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {orgBreakdown.map((org, index) => (
                    <div 
                      key={org.org_id}
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ 
                        background: index % 2 === 0 ? currentColors.background : currentColors.cardBg 
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                          style={{ 
                            background: `linear-gradient(135deg, ${currentColors.accentBlue}, ${currentColors.accentPurple})` 
                          }}
                        >
                          {index + 1}
                        </span>
                        <span 
                          className="font-medium text-sm"
                          style={{ color: currentColors.textPrimary }}
                        >
                          {org.org_name}
                        </span>
                      </div>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-bold"
                        style={{ 
                          background: `${currentColors.accentGreen}20`,
                          color: currentColors.accentGreen
                        }}
                      >
                        {org.user_count} users
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold mb-4" style={{ color: currentColors.textPrimary }}>
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Add Organization', icon: Building2, color: currentColors.accentBlue, onClick: () => navigate('/organizations') },
                { label: 'Create Outlet', icon: Store, color: currentColors.accentGreen, onClick: () => navigate('/outlets') },
                { label: 'Invite User', icon: Users, color: currentColors.accentPurple, onClick: () => navigate('/users') },
                { label: 'View Insights', icon: Activity, color: currentColors.accentOrange, onClick: () => navigate('/intelligence') },
              ].map((action) => (
                <motion.button
                  key={action.label}
                  onClick={action.onClick}
                  className="rounded-xl p-4 flex flex-col items-center gap-2"
                  style={{
                    background: currentColors.cardBg,
                    boxShadow: getNeumorphicShadow(theme),
                  }}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: getNeumorphicHover(theme),
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <action.icon size={24} style={{ color: action.color }} />
                  <span className="text-sm font-medium" style={{ color: currentColors.textPrimary }}>
                    {action.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
